import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";
const HTTP_UNAUTHORIZED = 401;
const IS_DEV = process.env.NODE_ENV !== "production";

function devWarn(...args) {
  if (IS_DEV) console.warn(...args);
}

/**
 * useAdminReferences — CRUD hook for /admin/references. Stores CSL-JSON
 * so citations can be rendered as APA, MLA (and more in the future)
 * on the backend.
 */
export default function useAdminReferences() {
  const [key] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const [references, setReferences] = useState([]);
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const authHeaders = () => ({ "X-Admin-Key": key });

  const fetchReferences = useCallback(async () => {
    if (!key) return;
    setStatus({ state: "loading", error: "" });
    try {
      const res = await axios.get(`${API}/admin/references`, {
        headers: authHeaders(),
      });
      setReferences(res.data || []);
      setStatus({ state: "ready", error: "" });
    } catch (err) {
      if (err?.response?.status === HTTP_UNAUTHORIZED) {
        setStatus({ state: "unauthorized", error: "Session expired." });
      } else {
        setStatus({ state: "error", error: "Could not load references." });
      }
    }
  }, [key]);

  useEffect(() => {
    if (key) fetchReferences();
  }, [key, fetchReferences]);

  const createReference = useCallback(
    async (payload) => {
      const res = await axios.post(`${API}/admin/references`, payload, {
        headers: authHeaders(),
      });
      setReferences((prev) => [res.data, ...prev]);
      return res.data;
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const patchReference = useCallback(
    async (id, patch) => {
      const res = await axios.patch(`${API}/admin/references/${id}`, patch, {
        headers: authHeaders(),
      });
      setReferences((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      return res.data;
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const deleteReference = useCallback(
    async (id) => {
      try {
        await axios.delete(`${API}/admin/references/${id}`, {
          headers: authHeaders(),
        });
        setReferences((prev) => prev.filter((r) => r.id !== id));
        return true;
      } catch (err) {
        devWarn("[VBB] deleteReference failed", err);
        return false;
      }
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const formatReference = useCallback(
    async (id, style = "apa") => {
      try {
        const res = await axios.get(
          `${API}/admin/references/${id}/format?style=${style}`,
          { headers: authHeaders() }
        );
        return res.data.citation;
      } catch (err) {
        devWarn("[VBB] formatReference failed", err);
        return "";
      }
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    key,
    references,
    status,
    fetchReferences,
    createReference,
    patchReference,
    deleteReference,
    formatReference,
  };
}
